import { Component, computed, inject, input } from '@angular/core';
import { IDocument } from '../../interfaces/i-document';
import { ChatDocumentService } from '../../pages/ai-chat/services/chat-document.service';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: 'attachment-doc',
  imports: [
    CommonModule,
    IconsModule,
    ButtonsModule,
    MatTooltipModule
  ],
  templateUrl: './attachment-doc.component.html',
  styleUrl: './attachment-doc.component.scss'
})
export class AttachmentDocComponent {
  document = input<IDocument>();

  chatDocumentService = inject(ChatDocumentService);

  readonly displayName = computed(() => {
    const name = this.document()?.name ?? '';
    return this.#ellipsizeFileName(name, { maxBaseLength: 20, minTail: 3 });
  });

  fileExtension(): string {
    const file = this.document()!.name.split('.');
    const fileExt = file[file.length - 1].toLowerCase();

    let className = '';

    switch (fileExt) {
      case 'doc': case 'docx': case 'dotx':
        className = 'word';
        break;
      case 'xls': case 'xlsx': case 'xltx':
        className = 'xls';
        break;
      case 'ppt': case 'pptx': case 'potx':
        className = 'ppt';
        break;
      case 'pdf':
        className = 'pdf';
        break;
      case 'png': case 'jpg': case 'jpeg': case 'webp': case 'gif': case 'svg':
        className = 'image';
        break;
      case 'sass': case 'scss': case 'css': case 'cs': case 'js':
      case 'jsx': case 'java': case 'py': case 'xml': case 'vue':
      case 'html': case 'jsp': case 'php': case 'sql': case 'yml':
      case 'yaml': case 'md': case 'log':
        className = 'code';
        break;
      case 'ts': case 'tsx':
        className = 'tscript';
        break;
      default:
        className = 'default';
        break;
    }

    return className;
  }

  #ellipsizeFileName(
    fullName: string,
    options: Readonly<{ maxBaseLength: number; minTail: number }>
  ): string {
    const { maxBaseLength, minTail } = options;

    const trimmed = fullName.trim();
    if (trimmed.length === 0) return '';

    const lastDot = trimmed.lastIndexOf('.');
    const hasExtension = lastDot > 0 && lastDot < trimmed.length - 1;

    const base = hasExtension ? trimmed.slice(0, lastDot) : trimmed;
    const ext = hasExtension ? trimmed.slice(lastDot) : '';

    if (base.length <= maxBaseLength) return `${base}${ext}`;

    const tail = base.slice(-Math.min(minTail, base.length));
    const headBudget = Math.max(1, maxBaseLength - minTail);
    const head = base.slice(0, headBudget);

    return `${head}...${tail}${ext}`;
  }
}
